import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Tab, Table, Tabs } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { DeveloperProjectTransferReason, DeveloperProjectTransferStatus, useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal, OverflowArea } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { Base64ToPDFBlob, parseAndLinkify, url_reverse } from "@components/Utils";

const rejectStatusesTransfer = [
    DeveloperProjectTransferStatus.REJECT_FIN,
    DeveloperProjectTransferStatus.REJECT_HOLDER,
    DeveloperProjectTransferStatus.REJECT_TECH,
];

const reqKind = {
    TRANSFER: "transfer",
    HOLDER: "holder",
    ARCHIVE: "archive",
};

export default function Transfer({ developerId, header }) {
    const { t } = useTranslation();
    const params = useParams();
    const { kind } = params;
    const { apiData } = useAppApi(url_reverse("api:developer:projects_transfer", { developer_id: developerId, kind }));
    const [showModalUploadFile, setShowModalUploadFile] = useState(false);
    const [showModalSendRequest, setShowModalSendRequest] = useState(null);
    const statePDF = useState("");
    const stateDelModal = useState(false);
    const navigate = useNavigate();
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");

    if (!apiData) return;

    const getTabTitleTransfer = () => {
        const developerIsTransfer = `${developerCommonInfo.developer.id}` === `${apiData.transfer?.dev_transfer_id}`;
        const showAlert = developerIsTransfer && rejectStatusesTransfer.includes(apiData.transfer?.moder_status);
        return (
            <>
                {t("Передача")} {showAlert && <Badge variant="warning">!</Badge>}
            </>
        );
    };

    const getTabTitleHolder = () => {
        const developerIsHolder =
            `${developerCommonInfo.developer.id}` === `${apiData.transfer?.dev_holder_id}` &&
            apiData.transfer?.moder_status !== DeveloperProjectTransferStatus.NEW;
        const showAlert = developerIsHolder && apiData.transfer?.moder_status === DeveloperProjectTransferStatus.HOLDER;
        return (
            <>
                {t("Приём")} {showAlert && <Badge variant="warning">!</Badge>}
            </>
        );
    };

    const handleSelect = (k) => {
        navigate({
            pathname: url_reverse("devs:projects_transfer", { dev_id: developerId, kind: k }),
        });
    };

    return (
        <CommonCard header={header}>
            <Tabs activeKey={kind} transition={false} className="react-bootstrap-tabs-fix" onSelect={handleSelect}>
                <Tab eventKey={reqKind.TRANSFER} title={getTabTitleTransfer()}>
                    {kind === reqKind.TRANSFER && (
                        <TabTransfering
                            setShowModalUploadFile={setShowModalUploadFile}
                            setShowModalSendRequest={setShowModalSendRequest}
                            statePDF={statePDF}
                            stateDelModal={stateDelModal}
                        />
                    )}
                </Tab>

                <Tab eventKey={reqKind.HOLDER} title={getTabTitleHolder()}>
                    {kind === reqKind.HOLDER && (
                        <TabReceiving
                            rejectStatusesTransfer={rejectStatusesTransfer}
                            setShowModalSendRequest={setShowModalSendRequest}
                            setShowModalUploadFile={setShowModalUploadFile}
                            statePDF={statePDF}
                            stateDelModal={stateDelModal}
                        />
                    )}
                </Tab>

                <Tab eventKey={reqKind.ARCHIVE} title={t("Архив")}>
                    {kind === reqKind.ARCHIVE && <TabArchive />}
                </Tab>
            </Tabs>

            <TransferDocumentFileUploadModal
                developerId={developerId}
                showModal={showModalUploadFile}
                setShowModal={setShowModalUploadFile}
                kind={kind}
            />

            {[reqKind.TRANSFER, reqKind.HOLDER].includes(kind) && (
                <DevModal
                    size="lg"
                    show={showModalSendRequest}
                    onHide={() => setShowModalSendRequest(false)}
                    header={t("Подтверждение отправки")}
                    body={
                        apiData.transfer && (
                            <>
                                <strong>{t("Передаются проекты")}:</strong>
                                <div className="mb-2">
                                    {apiData.transfering_games?.map((el) => (
                                        <div key={`tg_${el.id}`}>
                                            <Link to={url_reverse("games:page", { game_id: el.game_id })}>
                                                {el.__game.__model_locale_object.name}
                                            </Link>
                                            {el.__game.__childs.map((ch) => (
                                                <div key={`tg_${ch.id}`}>
                                                    <Link to={url_reverse("games:page", { game_id: ch.id })}>
                                                        {ch.__model_locale_object.name}
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-2">
                                    <strong>{t("Новому правообладателю")}:</strong> ID {apiData.transfer.dev_holder_id}
                                </div>

                                <div className="mb-2">
                                    <strong>{t("На основании")}:</strong>{" "}
                                    {useChoiceLabel("DeveloperProjectTransferReason", apiData)[apiData.transfer.reason]}
                                </div>

                                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                                    <div className="mb-2">
                                        <strong>{t("Договора")}:</strong> {apiData.transfer.contract_number} {t("от")}{" "}
                                        {apiData.transfer.contract_date}
                                    </div>
                                )}

                                <div className="mb-2">
                                    <strong>{t("Дата переноса")}:</strong> {apiData.transfer.time_transfer}
                                </div>

                                {(kind === reqKind.HOLDER ||
                                    (kind === reqKind.TRANSFER &&
                                        apiData.forms_data.main.data.reason !==
                                            DeveloperProjectTransferReason.TAX)) && (
                                    <>
                                        <div className="mb-3">
                                            <strong>{t("Загружен документ")}:</strong>
                                            <iframe
                                                src={statePDF[0]}
                                                width="100%"
                                                height="400px"
                                                className="mt-2 border"
                                                title={t("Предпросмотр документа")}
                                            />
                                        </div>

                                        <div className="alert alert-info">
                                            <strong>{t("Внимание!")}</strong>{" "}
                                            {t(
                                                "Пожалуйста, подтвердите, что эти данные совпадают с содержимым загруженного документа"
                                            )}
                                            .
                                        </div>
                                    </>
                                )}
                            </>
                        )
                    }
                    footer={
                        <DevForm.Button
                            variant="primary"
                            onClick={(ev) => {
                                showModalSendRequest.onClick(ev);
                                setShowModalSendRequest(null);
                            }}
                            name="action"
                            value="transfer_to_done"
                        >
                            {t("Подтверждаю соответствие данных")}
                        </DevForm.Button>
                    }
                />
            )}

            <TransferCancelModal stateDelModal={stateDelModal} />
        </CommonCard>
    );
}

const TabTransfering = ({ setShowModalUploadFile, setShowModalSendRequest, statePDF, stateDelModal }) => {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const onFormSubmit = useSubmit();
    const [showModalGameAdd, setShowModalGameAdd] = useState(false);
    const [search, setSearch] = useState("");
    const [PDFPreview, setPDFPreview] = statePDF;
    const [transferingGames, setTransferingGames] = useState([]);
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");

    const canEditStatuses = [
        DeveloperProjectTransferStatus.NEW,
        DeveloperProjectTransferStatus.REJECT_HOLDER,
        DeveloperProjectTransferStatus.REJECT_FIN,
        DeveloperProjectTransferStatus.REJECT_TECH,
    ];

    const disableEdit = apiData.transfer && !canEditStatuses.includes(apiData.transfer.moder_status);

    useEffect(() => {
        const PDFPreviewBlob = apiData?.file_signed_transfer?.url
            ? URL.createObjectURL(Base64ToPDFBlob(`data:application/pdf;base64,${apiData?.file_signed_transfer?.url}`))
            : "";
        setPDFPreview(PDFPreviewBlob);
    }, [apiData?.file_signed_transfer?.url]);

    useEffect(() => {
        if (apiData.forms_data.main.data.games) {
            const tg =
                apiData.available_games
                    ?.filter((game) => apiData.forms_data.main.data.games.includes(game.id.toString()))
                    ?.map((game) => game) || [];
            setTransferingGames(tg);
        } else {
            setTransferingGames([]);
        }
    }, [JSON.stringify(apiData)]);

    const handleApplyTransferGame = () => {
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.games = (
            apiData.available_games?.filter((game) => game.chosen)?.map((game) => game.id.toString()) || []
        ).join(",");
        setApiData(newApiData);
        setShowModalGameAdd(false);
    };

    const handleDeleteTransferGame = (game_id) => {
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.games = transferingGames
            .filter((game) => game.id !== game_id)
            .map((game) => game.id)
            .join(",");
        setApiData(newApiData);
    };

    if (apiData.transfer && developerCommonInfo.developer.id === apiData.transfer.dev_holder_id) {
        return (
            <div className="dmg-callout dmg-callout-info">
                {t(
                    "В данный момент вкладка недоступна поскольку вы находитесь на этапе приёма игр от другого разработчика"
                )}
            </div>
        );
    }

    return (
        <>
            {apiData.transfer?.moder_status === DeveloperProjectTransferStatus.REJECT_HOLDER && (
                <div className="dmg-callout dmg-callout-warning">
                    <h6>{t("Комментарий модератора")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        <Trans>
                            Невозможно отправить заявку: до указанной даты переноса осталось менее 5 рабочих дней.
                            Согласно правилам, дата переноса должна быть выбрана минимум за 5 рабочих дней до её
                            наступления. Заявка отклонена автоматически"
                        </Trans>
                    </div>
                </div>
            )}
            {apiData.transfer?.moder_comment && rejectStatusesTransfer.includes(apiData.transfer?.moder_status) && (
                <div className="dmg-callout dmg-callout-warning">
                    <h6>{t("Комментарий модератора")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        {apiData.transfer?.moder_comment.split("\n").map((o, i) => {
                            return <div key={`mc_${i}`}>{parseAndLinkify(o + "\n")}</div>;
                        })}
                    </div>
                </div>
            )}
            {disableEdit ? (
                apiData.transfer?.moder_status === DeveloperProjectTransferStatus.APPROVED ? (
                    <Alert variant="info">
                        {t("Запрос успешно обработан. Проекты будут перенесены в запланированное время.")}
                    </Alert>
                ) : (
                    <Alert variant="info">{t("Запрос в обработке.")}</Alert>
                )
            ) : (
                <div className="dmg-callout dmg-callout-info mb-2">
                    <Trans i18nKey="trans_dev_projects_transfer_transfering">
                        Вы можете перенести проекты в кабинет другого разработчика.
                        <br />
                        <b>ОБРАТИТЕ ВНИМАНИЕ</b>: перенос сопровождается предоставлением гарантийного письма о передаче
                        авторских прав на проекты новому правообладателю
                    </Trans>
                </div>
            )}
            <Form onSubmit={onFormSubmit}>
                <DevForm.Field prefix="main" name="games" />
                <DevForm.Field prefix="main" name="developer_id" />
                <DevForm.Field prefix="main" name="reason" />
                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                    <DevForm.Field prefix="main" name="contract_number" />
                )}
                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                    <DevForm.Field prefix="main" name="contract_date" />
                )}
                <DevForm.Field prefix="main" name="time_transfer" />
                <DevForm.Field prefix="main" name="transfer_comment" />

                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                    <>
                        <div className="my-3">
                            <Button
                                size="sm"
                                variant="dark"
                                onClick={() => setShowModalGameAdd(true)}
                                disabled={disableEdit}
                            >
                                {t("Выберите проекты для переноса")}
                            </Button>
                        </div>
                        {transferingGames?.length > 0 && (
                            <Table responsive className="dmg-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>{t("Проекты")}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {transferingGames.map((el) => (
                                        <React.Fragment key={`object_list_${el.id}`}>
                                            <tr>
                                                <td>{el.id}</td>
                                                <td>
                                                    <Link to={url_reverse("games:page", { game_id: el.id })}>
                                                        {el.__model_locale_object.name}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {!disableEdit && (
                                                        <Awesome
                                                            type="trash-o"
                                                            color="red"
                                                            onClick={() => handleDeleteTransferGame(el.id)}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                            {el.__childs.map((ch) => {
                                                return (
                                                    <tr key={`object_list_ch_${ch.id}`}>
                                                        <td>{ch.id}</td>
                                                        <td>
                                                            <Link to={url_reverse("games:page", { game_id: ch.id })}>
                                                                {ch.__model_locale_object.name}
                                                            </Link>
                                                        </td>
                                                        <td />
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        )}

                        <div className="my-5">
                            <div className="dmg-callout dmg-callout-info mb-2">
                                <Trans i18nKey="trans_dev_projects_transfer_scan">
                                    Скачайте шаблон, подпишите и приложите скан.
                                    <br />
                                    <b>ОБРАТИТЕ ВНИМАНИЕ</b>: в шаблон автоматически добавляются указанные выше данные:
                                    о новом правообладателе, проектах на перенос, основании переноса и дате переноса.
                                </Trans>
                            </div>
                            {apiData?.declined > 0 && (
                                <Alert variant="danger">
                                    {t(
                                        "Модератор нашел ошибку в документах. Исправьте ошибку и отправьте документы повторно."
                                    )}
                                </Alert>
                            )}
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>{t("Документ")}</th>
                                        <th>{t("Статус")}</th>
                                        <th>{t("Скачать")}</th>
                                        <th>{t("Загрузить файлы")}</th>
                                        <th>{t("Дата загрузки")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            {apiData.transfer?.reason === DeveloperProjectTransferReason.COPYRIGHT &&
                                                t("Гарантийное письмо о полной передаче прав и переносе проектов")}
                                            {apiData.transfer?.reason === DeveloperProjectTransferReason.LICENSE &&
                                                t("Гарантийное письмо о передаче лицензии и переносе проектов")}
                                            {apiData.transfer?.reason === DeveloperProjectTransferReason.TAX &&
                                                t("Гарантийное письмо о смене налогового статуса")}
                                        </td>
                                        <td>
                                            {apiData?.file_signed_transfer?.name ? (
                                                <Badge variant="warning">{t("Подписано")}</Badge>
                                            ) : (
                                                <Badge variant="success">{t("Подписать")}</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <ExportButton
                                                size="sm"
                                                buildReport="generate_template"
                                                buttonText={<Awesome type="download" color="white" />}
                                                disabled={
                                                    disableEdit || !apiData.transfer || transferingGames.length === 0
                                                }
                                                tooltip={
                                                    (!apiData.transfer || transferingGames.length === 0) &&
                                                    t("Необходимо выбрать хотя бы 1 игру и сохранить заявку")
                                                }
                                            />
                                        </td>
                                        <td>
                                            <DevForm.Button
                                                variant="primary"
                                                disabled={
                                                    disableEdit || !apiData.transfer || transferingGames.length === 0
                                                }
                                                tooltip={
                                                    (!apiData.transfer || transferingGames.length === 0) &&
                                                    t("Необходимо выбрать хотя бы 1 игру и сохранить заявку")
                                                }
                                                size="sm"
                                                name="upload"
                                                onClick={() => setShowModalUploadFile(true)}
                                            >
                                                <Awesome type="upload" color="white" />
                                            </DevForm.Button>
                                        </td>
                                        <td>{apiData?.file_signed_transfer?.date_modified}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>

                        {apiData.file_signed_transfer?.url && <iframe src={PDFPreview} width="100%" height="600px" />}
                    </>
                )}

                {!disableEdit && (
                    <>
                        <DevForm.Button variant="primary" type="submit" name="action" value="save">
                            {t("Сохранить")}
                        </DevForm.Button>{" "}
                        <DevForm.Button
                            variant="warning"
                            name="transfer_to_done"
                            disabled={
                                !apiData.transfer ||
                                (apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX &&
                                    !apiData.file_signed_transfer?.name)
                            }
                            tooltip={
                                apiData.transfer
                                    ? apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX &&
                                      !apiData.file_signed_transfer?.name &&
                                      t("Необходимо подписать гарантийное письмо")
                                    : t("Необходимо сохранить заявку")
                            }
                            onClick={() => {
                                setPDFPreview(PDFPreview);
                                setShowModalSendRequest({
                                    onClick: (ev) =>
                                        onFormSubmit(ev, {
                                            backUrl: url_reverse("devs:projects_transfer", {
                                                dev_id: developerCommonInfo.developer.id,
                                                kind: reqKind.TRANSFER,
                                            }),
                                        }),
                                });
                            }}
                        >
                            {t("Отправить запрос")}
                        </DevForm.Button>{" "}
                    </>
                )}
                {apiData.transfer && (
                    <DevForm.Button
                        variant="danger"
                        name="cancel_transfer"
                        onClick={() => stateDelModal[1]({ tab: "transfer" })}
                        className="float-right"
                    >
                        {t("Отменить")}
                    </DevForm.Button>
                )}
            </Form>

            <DevModal
                size="lg"
                show={showModalGameAdd}
                onHide={() => setShowModalGameAdd(false)}
                header={t("Выбрать игры")}
                body={
                    <>
                        <Row className="mb-2">
                            <Col md={12}>
                                <DevForm.TextInput
                                    autoFocus
                                    value={search}
                                    placeholder={t("Поиск")}
                                    onChange={(ev) => setSearch(ev.target.value)}
                                />
                            </Col>
                        </Row>
                        <OverflowArea y={500}>
                            <Table size="sm" hover responsive className="mt-2">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>ID</th>
                                        <th width="90%">{t("Название")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {apiData.available_games?.map((game, ind) => {
                                        if (
                                            search &&
                                            game.__model_locale_object.name
                                                .toLowerCase()
                                                .indexOf(search.toLowerCase()) < 0
                                        ) {
                                            return null;
                                        }
                                        return (
                                            <React.Fragment key={game.id}>
                                                <tr>
                                                    <td>
                                                        <DevForm.Checkbox
                                                            checked={game.chosen || false}
                                                            onChange={(ev) => {
                                                                const newApiData = { ...apiData };
                                                                newApiData.available_games[ind].chosen =
                                                                    ev.target.checked;
                                                                setApiData(newApiData);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>{game.id}</td>
                                                    <td>
                                                        <Link to={url_reverse("games:page", { game_id: game.id })}>
                                                            {game.__model_locale_object.name}
                                                        </Link>
                                                    </td>
                                                </tr>
                                                {game.__childs.map((ch) => {
                                                    return (
                                                        <tr key={ch.id}>
                                                            <td />
                                                            <td>{ch.id}</td>
                                                            <td>
                                                                <Link
                                                                    to={url_reverse("games:page", { game_id: ch.id })}
                                                                >
                                                                    {ch.__model_locale_object.name}
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </OverflowArea>
                    </>
                }
                footer={
                    <Button variant="primary" onClick={handleApplyTransferGame}>
                        {t("Применить")}
                    </Button>
                }
            />
        </>
    );
};

const TabReceiving = ({ setShowModalSendRequest, setShowModalUploadFile, statePDF, stateDelModal }) => {
    const { api_post } = useRequest();
    const { t } = useTranslation();
    const onFormSubmit = useSubmit();

    const { apiData, setApiData, apiUrl } = useAppContext();
    const [selectedPublishers, setSelectedPublishers] = useState({});
    const [selectedGamesDev, setSelectedGamesDev] = useState({});
    const [PDFPreview, setPDFPreview] = statePDF;
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");

    useEffect(() => {
        const PDFPreviewBlob = apiData?.file_signed_holder?.url
            ? URL.createObjectURL(Base64ToPDFBlob(`data:application/pdf;base64,${apiData?.file_signed_holder?.url}`))
            : "";
        setPDFPreview(PDFPreviewBlob);
    }, [apiData?.file_signed_holder?.url]);

    useEffect(() => {
        setSelectedPublishers(
            (apiData.transfering_games || []).reduce((acc, el) => {
                acc[el.game_id] = el.publishers.split(",");
                return acc;
            }, {})
        );
        setSelectedGamesDev(
            (apiData.transfering_games || []).reduce((acc, el) => {
                acc[el.game_id] = el.gamedevs.split(",");
                return acc;
            }, {})
        );
    }, []);

    const hasEmptyValues = (obj) =>
        Object.values(obj)
            .flat()
            .some((value) => !value || value.length === 0);

    const saveGameInfo = (ev) => {
        if (hasEmptyValues(selectedGamesDev) || hasEmptyValues(selectedPublishers)) {
            alert(t("Необходимо заполнить поля «Издатели» и «Разработчик» для всех игр"));
            return;
        }
        if (confirm(t("Подтвердите своё действие"))) {
            api_post(ev, apiUrl, {
                params: {
                    action: "save_game_info",
                    gamsedev: JSON.stringify(selectedGamesDev),
                    publishers: JSON.stringify(selectedPublishers),
                },
                then: (resp) => {
                    if (resp.status === "ok") {
                        const newApiData = { ...apiData };
                        newApiData.transfer.date_of_acceptance = resp.date_of_acceptance;
                        setApiData(newApiData);
                    }
                },
            });
        }
    };

    if (
        !apiData.transfer ||
        developerCommonInfo.developer.id !== apiData.transfer.dev_holder_id ||
        [DeveloperProjectTransferStatus.NEW, DeveloperProjectTransferStatus.DONE].includes(
            apiData.transfer.moder_status
        )
    ) {
        return (
            <div className="dmg-callout dmg-callout-info">
                {t("Во вкладке будут появляться заявки на перенос проектов в ваш кабинет.")}
            </div>
        );
    }

    const disableEdit = apiData.transfer.moder_status !== DeveloperProjectTransferStatus.HOLDER;

    return (
        <>
            {apiData.transfer.moder_status === DeveloperProjectTransferStatus.REJECT_HOLDER && (
                <div className="dmg-callout dmg-callout-warning">
                    <h6>{t("Комментарий модератора")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        <Trans>
                            Невозможно отправить заявку: до указанной даты переноса осталось менее 5 рабочих дней.
                            Согласно правилам, дата переноса должна быть выбрана минимум за 5 рабочих дней до её
                            наступления. Заявка отклонена автоматически"
                        </Trans>
                    </div>
                </div>
            )}

            {apiData.transfer.moder_comment && rejectStatusesTransfer.includes(apiData.transfer.moder_status) && (
                <div className="dmg-callout dmg-callout-warning">
                    <h6>{t("Комментарий модератора")}</h6>
                    <div className="font-weight-light show-wrap-text">
                        {apiData.transfer.moder_comment.split("\n").map((o, i) => {
                            return <div key={`mc_${i}`}>{parseAndLinkify(o + "\n")}</div>;
                        })}
                    </div>
                </div>
            )}
            {disableEdit ? (
                apiData.transfer?.moder_status === DeveloperProjectTransferStatus.APPROVED ? (
                    <Alert variant="info">
                        {t("Запрос успешно обработан. Проекты будут перенесены в запланированное время.")}
                    </Alert>
                ) : (
                    <Alert variant="info">{t("Запрос в обработке.")}</Alert>
                )
            ) : (
                <>
                    <div className="dmg-callout dmg-callout-info">
                        <Trans i18nKey="trans_dev_projects_transfer_receiving">
                            Вы получили запрос на перенос проектов в ваш кабинет.
                            <br />
                            Как его принять?
                            <br />
                            1. Выберите новые наименования полей «Разработчики» и «Издатели» для каждого переносимого
                            проекта. Нажмите кнопку «Подтвердить перенос проектов».
                            <br />
                            2. Подтвердите авторские права, подписав гарантийное письмо и приложив скан.
                            <br />
                            3. Отправьте заявку на перенос в модерацию
                        </Trans>
                    </div>
                </>
            )}
            <Form onSubmit={onFormSubmit}>
                <DevForm.Field prefix="main" name="reason" />
                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                    <DevForm.Field prefix="main" name="contract_number" />
                )}
                {apiData.forms_data.main.data.reason !== DeveloperProjectTransferReason.TAX && (
                    <DevForm.Field prefix="main" name="contract_date" />
                )}
                <DevForm.Field prefix="main" name="time_transfer" />
                <DevForm.Field prefix="main" name="holder_comment" />

                <Table responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Проекты")}</th>
                            <th>Издатели {<span className="asteriskField">*</span>}</th>
                            <th>Разработчики {<span className="asteriskField">*</span>}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apiData?.transfering_games?.map((el, i) => (
                            <React.Fragment key={`object_list${i}`}>
                                <tr>
                                    <td>
                                        <Link to={url_reverse("games:page", { game_id: el.game_id })}>
                                            {el.__game.__model_locale_object.name}
                                        </Link>
                                    </td>
                                    <td>
                                        <DevForm.SelectMultiple
                                            name={`publishers_${el.id}`}
                                            disabled={disableEdit || apiData.transfer.date_of_acceptance}
                                            choices={apiData?.publishers.map((o) => ({ value: o.id, label: o.name }))}
                                            value={selectedPublishers[el.game_id]}
                                            menuPortalTarget={document.body}
                                            onChange={(vals) =>
                                                setSelectedPublishers((prev) => ({ ...prev, [el.game_id]: vals }))
                                            }
                                        />
                                    </td>
                                    <td>
                                        <DevForm.SelectMultiple
                                            name={`gamesdev_${el.id}`}
                                            disabled={disableEdit || apiData.transfer.date_of_acceptance}
                                            choices={apiData?.gamedevs.map((o) => ({ value: o.id, label: o.name }))}
                                            value={selectedGamesDev[el.game_id]}
                                            menuPortalTarget={document.body}
                                            onChange={(vals) =>
                                                setSelectedGamesDev((prev) => ({ ...prev, [el.game_id]: vals }))
                                            }
                                        />
                                    </td>
                                </tr>
                                {el.__game.__childs.map((ch) => (
                                    <tr key={`tg_${ch.id}`}>
                                        <td>
                                            <Link to={url_reverse("games:page", { game_id: ch.id })}>
                                                {ch.__model_locale_object.name}
                                            </Link>
                                        </td>
                                        <td />
                                        <td />
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
                <div>
                    {apiData.transfer.date_of_acceptance ? (
                        <div className="dmg-callout dmg-callout-info">
                            {t("Дата принятия игр:")} {apiData.transfer.date_of_acceptance}
                        </div>
                    ) : (
                        <Button size="sm" variant="dark" disabled={disableEdit} onClick={saveGameInfo}>
                            {t("Подтвердить перенос проектов")}
                        </Button>
                    )}
                </div>
                <div className="my-5">
                    <div className="dmg-callout dmg-callout-info mb-2">
                        <Trans i18nKey="trans_dev_projects_transfer_scan">
                            Скачайте шаблон, подпишите и приложите скан.
                            <br />
                            <b>ОБРАТИТЕ ВНИМАНИЕ</b>: в шаблон автоматически добавляются указанные выше данные: о новом
                            правообладателе, проектах на перенос, основании переноса и дате переноса.
                        </Trans>
                    </div>
                    {apiData?.declined > 0 && (
                        <Alert variant="danger">
                            {t("Модератор нашел ошибку в документах. Исправьте ошибку и отправьте документы повторно.")}
                        </Alert>
                    )}
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>{t("Документ")}</th>
                                <th>{t("Статус")}</th>
                                <th>{t("Скачать")}</th>
                                <th>{t("Загрузить файлы")}</th>
                                <th>{t("Дата загрузки")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {apiData.transfer.reason === DeveloperProjectTransferReason.COPYRIGHT
                                        ? t("Гарантийное письмо о полной передаче прав и переносе проектов")
                                        : t("Гарантийное письмо о передаче лицензии и переносе проектов")}
                                </td>
                                <td>
                                    {apiData?.file_signed_holder?.name ? (
                                        <Badge variant="warning">{t("Подписано")}</Badge>
                                    ) : (
                                        <Badge variant="success">{t("Подписать")}</Badge>
                                    )}
                                </td>
                                <td>
                                    <ExportButton
                                        size="sm"
                                        buildReport="generate_template"
                                        buttonText={<Awesome type="download" color="white" />}
                                        disabled={disableEdit || !apiData.transfer.date_of_acceptance}
                                        tooltip={
                                            !apiData.transfer.date_of_acceptance &&
                                            t(
                                                "Необходимо подтвердить перенос проектов (нажать кнопку «Подтвердить перенос проектов»)"
                                            )
                                        }
                                    />
                                </td>
                                <td>
                                    <DevForm.Button
                                        variant="primary"
                                        size="sm"
                                        name="upload"
                                        disabled={disableEdit || !apiData.transfer.date_of_acceptance}
                                        tooltip={
                                            !apiData.transfer.date_of_acceptance &&
                                            t(
                                                "Необходимо подтвердить перенос проектов (нажать кнопку «Подтвердить перенос проектов»)"
                                            )
                                        }
                                        onClick={() => setShowModalUploadFile(true)}
                                    >
                                        <Awesome type="upload" color="white" />
                                    </DevForm.Button>
                                </td>
                                <td>{apiData?.file_signed_holder?.date_modified}</td>
                            </tr>
                        </tbody>
                    </Table>
                </div>

                {apiData?.file_signed_holder?.url && <iframe src={PDFPreview} width="100%" height="600px" />}

                {!disableEdit && (
                    <>
                        <DevForm.Button variant="primary" type="submit" name="action" value="save">
                            {t("Сохранить")}
                        </DevForm.Button>{" "}
                        <DevForm.Button
                            variant="warning"
                            name="transfer_to_done"
                            disabled={!apiData.file_signed_holder?.name}
                            tooltip={
                                !apiData.file_signed_holder?.name &&
                                t("Для отправки заявки необходимо загрузить подписанный документ")
                            }
                            onClick={() => {
                                setPDFPreview(PDFPreview);
                                setShowModalSendRequest({
                                    onClick: (ev) =>
                                        onFormSubmit(ev, {
                                            backUrl: url_reverse("devs:projects_transfer", {
                                                dev_id: developerCommonInfo.developer.id,
                                                kind: reqKind.HOLDER,
                                            }),
                                        }),
                                });
                            }}
                        >
                            {t("Отправить запрос")}
                        </DevForm.Button>{" "}
                    </>
                )}
                {apiData.transfer && (
                    <DevForm.Button
                        variant="danger"
                        name="cancel_transfer"
                        onClick={() => stateDelModal[1]({ tab: "holder" })}
                        className="float-right"
                    >
                        {t("Отменить")}
                    </DevForm.Button>
                )}
            </Form>
        </>
    );
};

const TabArchive = () => {
    const { t } = useTranslation();
    const { apiData } = useAppContext();

    return (
        <>
            <p>{t("В разделе хранятся подписанные документы по передаче и приёму проектов.")}</p>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Дата переноса")}</th>
                        <th>{t("Компании")}</th>
                        <th>{t("Игры")}</th>
                        <th>{t("Документы")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.archive?.map((o) => (
                        <tr key={`archive_${o.id}`}>
                            <td>{o.transfer.time_transfer}</td>
                            <td>
                                <Link to={url_reverse("devs:edit", { dev_id: o.transfer.dev_transfer_id })}>
                                    {o.transfer.dev_transfer.name}
                                </Link>
                                {" -> "}
                                <Link to={url_reverse("devs:edit", { dev_id: o.transfer.dev_holder_id })}>
                                    {o.transfer.dev_holder.name}
                                </Link>
                            </td>
                            <td>
                                {o.games.map((g) => (
                                    <div key={`game_${g.id}`}>
                                        <Link to={url_reverse("games:page", { game_id: g.id })}>
                                            {g.base_locale_object.name}
                                        </Link>
                                    </div>
                                ))}
                            </td>
                            <td>
                                {o.file_signed_transfer && (
                                    <>
                                        <a
                                            href={URL.createObjectURL(Base64ToPDFBlob(o.file_signed_transfer.url))}
                                            target="_blank"
                                        >
                                            {t("ГП от отчуждателя")}
                                        </a>
                                        <br />
                                    </>
                                )}
                                <a
                                    href={URL.createObjectURL(Base64ToPDFBlob(o.file_signed_holder.url))}
                                    target="_blank"
                                >
                                    {t("ГП от правообладателя")}
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
};

const TransferDocumentFileUploadModal = ({ developerId, showModal, setShowModal, kind }) => {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const onFormSubmit = useSubmit({
        submitUrl: url_reverse("api:developer:projects_transfer:upload", { developer_id: developerId }),
    });

    return (
        <DevModal
            show={showModal}
            onHide={() => setShowModal(false)}
            header={t("Документы")}
            body={<RenderForm prefix="upload" apiData={apiData} setApiData={setApiData} bareForm={true} />}
            footer={
                <DevForm.Button variant="primary" type="submit" name="submit">
                    {t("Подтвердить и отправить")}
                </DevForm.Button>
            }
            renderWrapper={(children) => (
                <Form
                    onSubmit={(ev) => {
                        ev.preventDefault();
                        if (!confirm(t("Подтвердите своё действие"))) {
                            return;
                        }
                        onFormSubmit(ev, {
                            submitPrefixes: ["upload"],
                            backUrl: url_reverse("devs:projects_transfer", {
                                dev_id: developerId,
                                kind,
                            }),
                            onFinally: () => setShowModal(false),
                        });
                    }}
                >
                    {children}
                </Form>
            )}
        />
    );
};

const TransferCancelModal = ({ stateDelModal }) => {
    const { t } = useTranslation();
    const { apiUrl, setApiReget } = useAppContext();
    const [show, setShow] = stateDelModal;
    const { api_post } = useRequest();

    const handleClose = () => {
        setShow(false);
    };
    const handleSubmit = (ev) => {
        ev.preventDefault();
        api_post(ev, apiUrl, {
            params: {
                action: "cancel_transfer",
                comment: show.comment,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiReget(true);
                    handleClose();
                }
            },
        });
    };
    return (
        <DevModal
            size="lg"
            show={show}
            onHide={handleClose}
            header={t("Подтвердите отмену")}
            body={
                show.tab === "holder" && (
                    <FormFieldGroup label={t("Комментарий для отчуждателя")} required={true}>
                        <DevForm.TextArea
                            name="comment"
                            value={show.comment || ""}
                            required={true}
                            onChange={(ev) => setShow((prev) => ({ ...prev, comment: ev.target.value }))}
                        />
                    </FormFieldGroup>
                )
            }
            footer={
                <DevForm.Button
                    variant="primary"
                    type="submit"
                    name="action"
                    value="cancel_transfer"
                    onClick={handleSubmit}
                >
                    {t("Продолжить")}
                </DevForm.Button>
            }
        />
    );
};
